import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import Add from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import { apiUrl, fetchJson } from '../../apiBase';

const TYPES = [
  { value: 'yardimci', label: 'Yardımcı Kitaplar' },
  { value: 'okuma', label: 'Okuma Kitapları' },
];

export default function BooksManager({ schoolId, onError, onSuccess, user }) {
  const [items, setItems] = useState([]);
  const [classItems, setClassItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterGrade, setFilterGrade] = useState('');
  const [filterType, setFilterType] = useState('');

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const [type, setType] = useState('yardimci');
  const [grade, setGrade] = useState([]);
  const [title, setTitle] = useState('');
  const [pageCount, setPageCount] = useState('');
  const [author, setAuthor] = useState('');
  const [publisher, setPublisher] = useState('');
  const [description, setDescription] = useState('');

  const token = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('adminUser') || '{}')?.token; } catch (e) { return undefined; }
  }, []);
  const authHeaders = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  const gradeOptions = useMemo(() => {
    const gradeSet = new Set();
    (classItems || []).forEach((cls) => {
      const gradeRaw = String(cls?.grade_level || '').trim();
      if (gradeRaw) {
        const digits = /^\d+$/.test(gradeRaw) ? gradeRaw : (gradeRaw.match(/\d+/)?.[0] || '');
        if (digits) gradeSet.add(String(Number(digits)));
      }
      const className = String(cls?.name || cls?.code || '').trim();
      const fromName = className.match(/^\s*(\d{1,2})/)?.[1] || '';
      if (fromName) gradeSet.add(String(Number(fromName)));
    });
    return Array.from(gradeSet).sort((a, b) => Number(a) - Number(b));
  }, [classItems]);

  const loadClasses = useCallback(async () => {
    try {
      const res = await fetchJson(apiUrl(`/admin/classes/${encodeURIComponent(schoolId)}`), { headers: { ...authHeaders } });
      if (!res.ok) return;
      const data = await res.json();
      setClassItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setClassItems([]);
    }
  }, [schoolId, authHeaders]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (filterGrade) q.set('grade', filterGrade);
      if (filterType) q.set('type', filterType);
      const res = await fetchJson(
        apiUrl(`/admin/books/${encodeURIComponent(schoolId)}${q.toString() ? `?${q}` : ''}`),
        { headers: { ...authHeaders } }
      );
      if (!res.ok) throw new Error('list');
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      onError && onError('Kitaplar yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [schoolId, filterGrade, filterType, onError, authHeaders]);

  useEffect(() => { if (schoolId) { load(); loadClasses(); } }, [schoolId, load, loadClasses]);

  const openNew = () => {
    setEditing(null);
    setType('yardimci');
    setGrade(filterGrade ? [filterGrade] : []);
    setTitle('');
    setPageCount('');
    setAuthor('');
    setPublisher('');
    setDescription('');
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setType(row.type || 'yardimci');
    setGrade(String(row.grade || '').split(',').map(x => x.trim()).filter(Boolean));
    setTitle(row.title || '');
    setPageCount(row.page_count == null ? '' : String(row.page_count));
    setAuthor(row.author || '');
    setPublisher(row.publisher || '');
    setDescription(row.description || '');
    setOpen(true);
  };

  const resetModal = () => { setOpen(false); setEditing(null); };

  const save = async () => {
    try {
      if (!type || !(grade || []).length || !title) { onError && onError('Lütfen zorunlu alanları doldurun'); return; }
      const normalizedPageCount = String(pageCount || '').trim() === '' ? null : Math.max(0, parseInt(pageCount, 10) || 0);
      const body = { type, grade: (grade || []).join(','), title, page_count: normalizedPageCount, author, publisher, description };
      let res;
      if (editing) {
        res = await fetchJson(apiUrl(`/admin/books/${encodeURIComponent(schoolId)}/${encodeURIComponent(editing.id)}`), {
          method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeaders }, body: JSON.stringify(body)
        });
      } else {
        res = await fetchJson(apiUrl(`/admin/books/${encodeURIComponent(schoolId)}`), {
          method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders }, body: JSON.stringify(body)
        });
      }
      if (!res.ok) throw new Error('save');
      onSuccess && onSuccess('Kitap kaydedildi');
      resetModal();
      load();
    } catch (e) { onError && onError('Kitap kaydedilemedi'); }
  };

  const remove = async (row) => {
    if (!window.confirm('Kitap silinsin mi?')) return;
    try {
  const res = await fetchJson(apiUrl(`/admin/books/${encodeURIComponent(schoolId)}/${encodeURIComponent(row.id)}`), { method: 'DELETE', headers: authHeaders });
      if (!res.ok) throw new Error('del');
      onSuccess && onSuccess('Kitap silindi');
      load();
    } catch (e) { onError && onError('Kitap silinemedi'); }
  };

  const typeChip = (t) => (
    <Chip size="small" label={t === 'yardimci' ? 'Yardımcı' : 'Okuma'} color={t === 'yardimci' ? 'primary' : 'success'} sx={{ fontWeight: 600 }} />
  );

  return (
    <>
      <Paper sx={{ p: 2, borderRadius: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <TextField select size="small" label="Sınıf (Seviye)" value={filterGrade} onChange={(e) => setFilterGrade(e.target.value)} fullWidth>
              <MenuItem value="">Tümü</MenuItem>
              {gradeOptions.map((g) => (<MenuItem key={g} value={g}>{g}. Sınıf</MenuItem>))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <TextField select size="small" label="Tür" value={filterType} onChange={(e) => setFilterType(e.target.value)} fullWidth>
              <MenuItem value="">Tümü</MenuItem>
              {TYPES.map((t) => (<MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4} md={6} sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
            <Button startIcon={<Add />} variant="contained" size="small" onClick={openNew}>
              Yeni Kitap Önerisi
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Kitap Önerileri</Typography>
        {loading ? (
          <List>
            {Array.from({ length: 6 }).map((_, i) => (
              <ListItem key={`s-${i}`} divider>
                <ListItemText
                  primary={<Skeleton variant="text" width="60%" />}
                  secondary={<Skeleton variant="text" width="40%" />}
                />
              </ListItem>
            ))}
          </List>
        ) : items.length === 0 ? (
          <Box sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="body1" sx={{ mb: 2 }}>Henüz kitap önerisi eklenmedi</Typography>
            <Button variant="contained" startIcon={<Add />} onClick={openNew}>+ Yeni Kitap</Button>
          </Box>
        ) : (
          <List>
            {items.map((it) => (
              <ListItem
                key={it.id}
                divider
                onClick={() => openEdit(it)}
                sx={{
                  borderRadius: 1,
                  '&:hover': { bgcolor: '#F9FAFB', cursor: 'pointer' },
                  transition: 'background-color 0.2s ease',
                }}
                secondaryAction={
                  <Box>
                    <IconButton
                      edge="end"
                      onClick={(e) => { e.stopPropagation(); openEdit(it); }}
                      sx={{ width: 32, height: 32, mr: 0.5, color: 'text.secondary', '&:hover': { bgcolor: '#F5F5F5', color: 'text.primary' } }}
                      aria-label="Düzenle"
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      edge="end"
                      onClick={(e) => { e.stopPropagation(); remove(it); }}
                      sx={{ width: 32, height: 32, color: 'text.secondary', '&:hover': { bgcolor: '#F5F5F5', color: 'text.primary' } }}
                      aria-label="Sil"
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                }
              >
                <ListItemText
                  primary={`${it.grade}. Sınıf • ${it.title} ${it.author ? '— ' + it.author : ''}`}
                  secondary={<>
                    {typeChip(it.type)}
                    <span style={{ marginLeft: 8, color: '#616161' }}>Sayfa: {it.page_count != null ? it.page_count : '-'}</span>
                    {it.publisher ? <span style={{ marginLeft: 8, color: '#616161' }}>{it.publisher}</span> : null}
                    {it.description ? <span style={{ marginLeft: 8, color: '#616161' }}>{it.description}</span> : null}
                  </>}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Kitap Önerisi</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField select label="Tür" value={type} onChange={(e) => setType(e.target.value)} size="small" fullWidth>
                {TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Sınıf (Seviye)"
                value={grade}
                onChange={(e) => setGrade(Array.isArray(e.target.value) ? e.target.value : String(e.target.value || '').split(',').filter(Boolean))}
                size="small"
                fullWidth
                SelectProps={{ multiple: true, renderValue: (selected) => (Array.isArray(selected) ? selected.map((g) => `${g}. Sınıf`).join(', ') : '') }}
              >
                {gradeOptions.map((g) => (
                  <MenuItem key={g} value={g}>{g}. Sınıf</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField label="Kitap Adı" value={title} onChange={(e) => setTitle(e.target.value)} size="small" fullWidth />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Sayfa Sayısı"
                value={pageCount}
                onChange={(e) => setPageCount(e.target.value)}
                size="small"
                fullWidth
                type="number"
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Yazar" value={author} onChange={(e) => setAuthor(e.target.value)} size="small" fullWidth />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Yayınevi" value={publisher} onChange={(e) => setPublisher(e.target.value)} size="small" fullWidth />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Açıklama" value={description} onChange={(e) => setDescription(e.target.value)} size="small" fullWidth multiline minRows={2} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>İptal</Button>
          <Button variant="contained" onClick={save}>Kaydet</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
